import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { captureFrame, liveCameraAvailable, MAX_SIDE, openRearCamera } from "../camera";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { LoadingPill, Spinner } from "../components/Spinner";
import { setTorch, torchSupported } from "../torch";

/** Re-encode to JPEG ≤ 1280 px: smaller upload, and drops EXIF (GPS) metadata. */
async function toJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const k = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * k);
  canvas.height = Math.round(bitmap.height * k);
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), "image/jpeg", 0.8),
  );
}

/**
 * Figma 07 "Photo du stationnement" → 07b "validate or retake". Live viewfinder in the page
 * (getUserMedia, like the QR scan: test of 14/10/2026, no Figma frame yet: the 07 instructions on
 * top, the viewfinder below). Falls back to the native camera (file input) when the live camera is
 * unavailable or refused. `onDone(photo)` gets the JPEG, or undefined when the rider skips it —
 * Pony never refuses FINISH without a photo.
 */
export function ParkingPhoto(props: {
  onBack: () => void;
  onDone: (photo: Blob | undefined) => void;
  busy: boolean;
}) {
  let input!: HTMLInputElement;
  let video!: HTMLVideoElement;
  const [photo, setPhoto] = createSignal<{ blob: Blob; url: string }>();
  const [error, setError] = createSignal<string>();
  const [preparing, setPreparing] = createSignal(false);
  /** Live viewfinder in use; false: the native camera (no HTTPS, refused, no camera). */
  const [live, setLive] = createSignal(liveCameraAvailable());
  const [ready, setReady] = createSignal(false);
  const [torchTrack, setTorchTrack] = createSignal<MediaStreamTrack>();
  const [torchOn, setTorchOn] = createSignal(false);
  let stopStream: (() => void) | undefined;

  const stopCamera = () => {
    stopStream?.();
    stopStream = undefined;
    setReady(false);
    setTorchTrack(undefined);
    setTorchOn(false);
  };
  const startCamera = async () => {
    if (!live() || stopStream) return;
    // Solid renders the viewfinder synchronously: the <video> exists after this tick. (Not a frame:
    // requestAnimationFrame never fires in a hidden page.)
    await Promise.resolve();
    try {
      const cam = await openRearCamera(video, true);
      stopStream = cam.stop;
      if (torchSupported(cam.track)) setTorchTrack(cam.track);
      setReady(true);
    } catch {
      stopCamera();
      setLive(false); // refused or no camera: the phone's camera app instead
    }
  };
  onMount(() => void startCamera());
  onCleanup(() => {
    stopCamera();
    const p = photo();
    if (p) URL.revokeObjectURL(p.url);
  });

  const show = (blob: Blob) => {
    const prev = photo();
    if (prev) URL.revokeObjectURL(prev.url);
    setPhoto({ blob, url: URL.createObjectURL(blob) });
  };
  const shoot = async () => {
    setError(undefined);
    try {
      show(await captureFrame(video));
      stopCamera(); // 07b: the camera light goes off while the rider reviews
    } catch {
      setError("La photo n'a pas pu être prise. Réessayez.");
    }
  };
  // Back from 07b returns to the camera (07); from 07, to the ride.
  const retake = () => {
    const p = photo();
    if (p) URL.revokeObjectURL(p.url);
    setPhoto(undefined);
    void startCamera();
  };
  const back = () => (photo() ? retake() : props.onBack());
  const pick = () => {
    setError(undefined);
    input.value = "";
    input.click();
  };
  const toggleTorch = async () => {
    const t = torchTrack();
    if (t && (await setTorch(t, !torchOn()))) setTorchOn(!torchOn());
  };
  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setPreparing(true);
    try {
      show(await toJpeg(file));
    } catch {
      setError("Impossible de lire la photo. Réessayez.");
    } finally {
      setPreparing(false);
    }
  };

  return (
    <main class="fixed inset-y-0 inset-x-0 mx-auto max-w-[430px] z-30 flex flex-col bg-surface">
      <AppBar title="Photo du stationnement" back={back} />
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        class="hidden"
        onChange={(e) => void onFile(e.currentTarget.files?.[0])}
      />
      <Show
        when={photo()}
        fallback={
          <Show
            when={live()}
            fallback={
              // Figma 07: instructions screen only; the camera opens on "Prendre la photo" (note C9).
              <div class="flex-1 min-h-0 flex flex-col px-[15px]">
                {/* Instruction card: white 94 %, radius 12, violet camera. */}
                <div class="mt-4 flex min-h-[66px] shrink-0 items-start gap-[11px] rounded-[12px] bg-white/94 pl-[14px] pr-3 py-[10px] short:mt-3">
                  <img
                    src="/icons/camera-violet-24.svg"
                    width="24"
                    height="24"
                    alt=""
                    class="mt-[11px] shrink-0"
                  />
                  <p class="text-sm leading-[1.25] tracking-[0.14px] text-strong">
                    Prenez une photo de votre vélo. Cela nous permet de valider le bon
                    stationnement.
                  </p>
                </div>
                <p class="mt-[22px] shrink-0 px-[5px] text-sm font-semibold leading-[1.25] tracking-[0.14px] text-strong short:mt-3">
                  Exemple de photo
                </p>
                {/* 372 x 500 in Figma; shrinks (object-cover) when the phone is shorter. */}
                <div class="relative mt-[10px] min-h-[120px] max-h-[500px] flex-1 overflow-hidden rounded-[12px]">
                  <img
                    src="/img/parking-photo-sample.jpg"
                    alt="Exemple : vélo garé en entier dans la zone de stationnement"
                    class="absolute inset-0 h-full w-full object-cover"
                  />
                  <Show when={preparing() || props.busy}>
                    <div class="absolute inset-0 flex items-center justify-center bg-white/40">
                      <LoadingPill
                        label={props.busy ? "Fin du trajet…" : "Préparation de la photo…"}
                      />
                    </div>
                  </Show>
                </div>
                <Show when={error()}>
                  {(m) => (
                    <p class="mt-2 px-[5px] text-sm text-[#e53935]" role="alert">
                      {m()}
                    </p>
                  )}
                </Show>
                <div
                  class="mt-auto shrink-0 pt-6 short:pt-3"
                  style={{ "padding-bottom": "max(0.5rem, env(safe-area-inset-bottom))" }}
                >
                  <Button onClick={pick} disabled={preparing() || props.busy}>
                    Prendre la photo
                  </Button>
                  {/* Not in Figma: Pony never refuses FINISH without a photo. Kept discreet. */}
                  <button
                    type="button"
                    class="mx-auto mt-1 flex h-8 items-center justify-center gap-2 px-3 text-[13px] leading-[1.25] text-text underline disabled:no-underline disabled:opacity-60"
                    disabled={props.busy}
                    aria-busy={props.busy || undefined}
                    onClick={() => props.onDone(undefined)}
                  >
                    <Show when={props.busy}>
                      <Spinner size={14} />
                    </Show>
                    Terminer sans photo
                  </button>
                </div>
              </div>
            }
          >
            <div class="flex-1 min-h-0 flex flex-col px-[15px]">
              {/* Figma 07 instruction card, then the live viewfinder (no Figma frame yet). */}
              <div class="mt-4 flex min-h-[66px] shrink-0 items-start gap-[11px] rounded-[12px] bg-white/94 pl-[14px] pr-3 py-[10px] short:mt-3">
                <img
                  src="/icons/camera-violet-24.svg"
                  width="24"
                  height="24"
                  alt=""
                  class="mt-[11px] shrink-0"
                />
                <p class="text-sm leading-[1.25] tracking-[0.14px] text-strong">
                  Prenez une photo de votre vélo. Cela nous permet de valider le bon stationnement.
                </p>
              </div>
              <div class="relative mt-3 min-h-[160px] flex-1 overflow-hidden rounded-[12px] bg-[#1a1a1f]">
                <video
                  ref={video}
                  class="absolute inset-0 h-full w-full object-cover"
                  muted
                  playsinline
                  aria-label="Viseur de l'appareil photo"
                />
                <Show when={!ready() || props.busy}>
                  <div class="absolute inset-0 flex items-center justify-center">
                    <LoadingPill
                      label={props.busy ? "Fin du trajet…" : "Ouverture de la caméra…"}
                    />
                  </div>
                </Show>
                {/* The example stays at hand, small, over the viewfinder. */}
                <figure class="absolute bottom-3 left-3 m-0 w-[66px] overflow-hidden rounded-[8px] border-2 border-white bg-white shadow">
                  <img
                    src="/img/parking-photo-sample.jpg"
                    alt="Exemple : vélo garé en entier dans la zone de stationnement"
                    class="block h-[88px] w-full object-cover"
                  />
                  <figcaption class="py-[2px] text-center text-[11px] font-medium leading-[1.25] text-strong">
                    Exemple
                  </figcaption>
                </figure>
                <Show when={torchTrack()}>
                  <button
                    type="button"
                    class={`absolute right-3 top-3 h-12 w-12 rounded-full flex items-center justify-center ${torchOn() ? "bg-green" : "bg-white"}`}
                    aria-label={torchOn() ? "Éteindre la lampe" : "Allumer la lampe"}
                    aria-pressed={torchOn()}
                    onClick={() => void toggleTorch()}
                  >
                    <img src="/icons/flashlight.svg" width="24" height="24" alt="" />
                  </button>
                </Show>
              </div>
              <Show when={error()}>
                {(m) => (
                  <p class="mt-2 px-[5px] text-sm text-[#e53935]" role="alert">
                    {m()}
                  </p>
                )}
              </Show>
              <div
                class="shrink-0 pt-3 flex flex-col items-center"
                style={{ "padding-bottom": "max(0.5rem, env(safe-area-inset-bottom))" }}
              >
                <button
                  type="button"
                  class="h-[68px] w-[68px] rounded-full active:scale-95 disabled:opacity-50"
                  disabled={!ready() || props.busy}
                  onClick={() => void shoot()}
                  aria-label="Prendre la photo"
                >
                  <img src="/icons/shutter-button.svg" width="68" height="68" alt="" />
                </button>
                {/* Not in Figma: Pony never refuses FINISH without a photo. Kept discreet. */}
                <button
                  type="button"
                  class="mt-1 flex h-8 items-center justify-center gap-2 px-3 text-[13px] leading-[1.25] text-text underline disabled:no-underline disabled:opacity-60"
                  disabled={props.busy}
                  aria-busy={props.busy || undefined}
                  onClick={() => props.onDone(undefined)}
                >
                  <Show when={props.busy}>
                    <Spinner size={14} />
                  </Show>
                  Terminer sans photo
                </button>
              </div>
            </div>
          </Show>
        }
      >
        {(p) => (
          <div class="flex-1 min-h-0 flex flex-col bg-white">
            <img
              src={p().url}
              alt="Photo du stationnement"
              class="flex-1 min-h-0 w-full object-cover"
            />
            {/* Figma 07b bottom panel: white, question, Valider / Reprendre. */}
            <div
              class="px-[15px] pt-[10px]"
              style={{ "padding-bottom": "max(30px, env(safe-area-inset-bottom))" }}
            >
              <p class="mx-[5px] text-center text-[13px] leading-[1.25] tracking-[0.13px] text-text">
                Le vélo est-il bien visible dans la zone de stationnement ?
              </p>
              <Button class="mt-[18px]" loading={props.busy} onClick={() => props.onDone(p().blob)}>
                Valider
              </Button>
              <Button
                variant="outline"
                class="mt-[10px]"
                disabled={props.busy}
                onClick={() => (live() ? retake() : pick())}
              >
                Reprendre
              </Button>
            </div>
          </div>
        )}
      </Show>
    </main>
  );
}
